import { PrintHeaderSize, PrintOrientation } from '@strength/domain';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { trpc } from '../../../../../trpc/react';
import { useFlags } from '../../../../context/featureFlag';
import { useViewer } from '../../../../context/viewer';
import { toast } from '../../../../hooks/useToast';
import { getLocale } from '../../../../utils/i18n';
import { usePlanContext } from '../../context/PlanContext';
import { useCalendarContext } from '../context/CalendarContext';
import { isHubEventsQueryEnabled } from '../hooks/useCalendarEvents';

// Short poll interval - the print worker typically finishes a calendar PDF
// within a couple of seconds, and this task list is only ever polled while
// the print drawer's mutation is in flight.
const PRINT_TASK_POLL_INTERVAL_MS = 2000;

// Upper bound on how long to poll a single print task. Guards against a
// worker crash/timeout that leaves the task row stuck in 'pending' (or a
// 'completed' row missing its pdfUrl) - without this, polling would run for
// the lifetime of the page and the UI would stay stuck on "generating".
const PRINT_TASK_TIMEOUT_MS = 120_000;

export type CalendarPrintOptionsInput = {
  orientation: PrintOrientation;
  fitToPage: boolean;
  headerText: string | null;
  headerSize: PrintHeaderSize;
  trackIds: string[];
};

export type CalendarPDFGenerationHook = {
  generateCalendarPDF: (printOptions: CalendarPrintOptionsInput) => void;
  pdfGenerating: boolean;
};

/**
 * Generates the Annual Planner Calendar print PDF (SC-4068).
 *
 * Unlike the Block Builder's `useGenerateWorkoutPDF` (which relies on
 * loco-sync to observe `BlockPrintTask` updates), this polls the plain tRPC
 * `calendar.fetchPrintTask` query - the calendar print task is not a synced
 * table, per this project's decision not to add new loco-sync tables ahead
 * of the Zero migration.
 */
export function useGenerateCalendarPDF(): CalendarPDFGenerationHook {
  const { t } = useTranslation();
  const { teamId, selectedAthlete, showCanceled } = usePlanContext();
  const { currentDate, deselectedHubEventTypeIds } = useCalendarContext();
  const { hubConnected } = useViewer();
  const { hubEventsOnCalendarFlag } = useFlags();
  const mutation = trpc.calendar.print.useMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitingOnPrintTaskId, setWaitingOnPrintTaskId] = useState<
    string | null
  >(null);

  const { data: printTask } = trpc.calendar.fetchPrintTask.useQuery(
    { id: waitingOnPrintTaskId ?? '' },
    {
      enabled: !!waitingOnPrintTaskId,
      refetchInterval: PRINT_TASK_POLL_INTERVAL_MS,
    },
  );

  useEffect(() => {
    if (!printTask) return;

    if (printTask.status === 'completed' && printTask.pdfUrl) {
      window.open(printTask.pdfUrl, '_blank');
      setWaitingOnPrintTaskId(null);
    } else if (printTask.status === 'failed') {
      setWaitingOnPrintTaskId(null);
      toast({
        title: t('somethingWentWrong'),
        severity: 'error',
      });
    }
  }, [printTask, t]);

  useEffect(() => {
    if (!waitingOnPrintTaskId) return;

    const timeoutId = setTimeout(() => {
      setWaitingOnPrintTaskId(null);
      toast({
        title: t('somethingWentWrong'),
        severity: 'error',
      });
    }, PRINT_TASK_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [waitingOnPrintTaskId, t]);

  const generateCalendarPDF = ({
    orientation,
    fitToPage,
    headerText,
    headerSize,
    trackIds,
  }: CalendarPrintOptionsInput) => {
    const printTaskId = v4();
    const dateRange = {
      start: currentDate.startOf('month').toISODate() ?? '',
      end: currentDate.endOf('month').toISODate() ?? '',
    };
    const athleteId = selectedAthlete?.id;
    // Only include Hub events in the PDF if they're actually visible on
    // screen right now - same gate the calendar page itself uses to decide
    // whether to query for them (see useCalendarEvents.ts).
    const includeHubEvents = isHubEventsQueryEnabled({
      teamId,
      startDate: dateRange.start,
      endDate: dateRange.end,
      hubConnected,
      hubEventsOnCalendarFlag,
    });

    setIsSubmitting(true);
    mutation.mutate(
      {
        printTaskId,
        teamId,
        trackIds,
        athleteId,
        dateRange,
        printOptions: {
          view: 'month',
          orientation,
          dateRange,
          trackIds,
          athleteId: athleteId ?? null,
          includeHubEvents,
          deselectedHubEventTypeIds: [...deselectedHubEventTypeIds],
          fitToPage,
          headerText,
          headerSize,
          showCanceled,
          locale: getLocale(),
        },
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          setWaitingOnPrintTaskId(printTaskId);
        },
        onError: () => {
          setIsSubmitting(false);
          toast({
            title: t('somethingWentWrong'),
            severity: 'error',
          });
        },
      },
    );
  };

  return {
    generateCalendarPDF,
    pdfGenerating: isSubmitting || !!waitingOnPrintTaskId,
  };
}
